// web/src/App.jsx
import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import LoginScreen     from './screens/LoginScreen';
import TablesScreen    from './screens/TablesScreen';
import OrderScreen     from './screens/OrderScreen';
import CartScreen      from './screens/CartScreen';
import ConfirmedScreen from './screens/ConfirmedScreen';
import MyOrdersScreen  from './screens/MyOrdersScreen';
import OwnerDashboardScreen from './screens/OwnerDashboardScreen';
import ForcedPinChangeScreen from './screens/ForcedPinChangeScreen';
import AiHelpButton    from './components/AiHelpButton';
import { hydrateFromAPI } from './db';
import { apiClient, setToken, getToken, setLogoutCallback, logoutUser } from './api';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// ─── Auth context ─────────────────────────────────────────────────────────────

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// ─── Router context ───────────────────────────────────────────────────────────

export const NavContext = createContext(null);
export const useNav = () => useContext(NavContext);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function decodeToken(token) {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (_) {
    return null;
  }
}

// ─── No Venue Screen ──────────────────────────────────────────────────────────
// Shown when someone hits the root URL with no /v/<slug> in the path.
// Staff should always arrive via their venue link or QR code.

function NoVenueScreen() {
  const [input, setInput] = useState('');

  const go = () => {
    const slug = input.trim().toLowerCase().replace(/\s+/g, '-');
    if (slug) window.location.href = `/v/${slug}`;
  };

  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
                  minHeight:'100vh', backgroundColor:'var(--bg)' }}>
      <div style={{ background:'var(--surface)', borderRadius:12, padding:32,
                    width:'100%', maxWidth:360, textAlign:'center' }}>
        <h1 style={{ color:'var(--orange)', fontSize:36, fontWeight:700,
                     margin:'0 0 8px', letterSpacing:2, fontFamily:"'Barlow Condensed', sans-serif" }}>
          ServeIQ
        </h1>
        <p style={{ color:'var(--text-muted)', fontSize:14, marginBottom:24, fontFamily:"'Barlow Condensed', sans-serif" }}>
          Open the link your manager sent you, or enter your venue name below.
        </p>
        <input
          style={{ width:'100%', padding:'12px 16px', background:'var(--surface-2)',
                   border:'1px solid var(--border)', borderRadius:8, color:'var(--text)',
                   fontSize:16, marginBottom:12, boxSizing:'border-box',
                   fontFamily:"'Barlow Condensed', sans-serif" }}
          placeholder="e.g. test-bar-3"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && go()}
        />
        <button
          onClick={go}
          style={{ width:'100%', padding:'14px 0', background:'var(--orange)',
                   border:'none', borderRadius:8, color:'#fff', fontSize:18,
                   fontWeight:700, cursor:'pointer', fontFamily:"'Barlow Condensed', sans-serif" }}
        >
          GO
        </button>
      </div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────

export default function App() {
  const [screen,        setScreen]        = useState('login');
  const [permissions,   setPermissions]   = useState({});
  const [params,        setParams]        = useState({});
  const [user,          setUser]          = useState(null);
  const [isOnline,      setIsOnline]      = useState(navigator.onLine);
  const [currentScreen, setCurrentScreen] = useState('tables');
  const [forcePinData,  setForcePinData]  = useState(null); // { currentPin } when force_pin_change

  // Detect if the URL contains a venue slug — computed once on load.
  // /v/test-bar-3 → true   |   / or /anything-else → false
  const isVenueUrl = window.location.pathname.startsWith('/v/');

  // Online/offline detection
  useEffect(() => {
    const handleOnline  = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online',  handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online',  handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const navigate = useCallback((targetScreen, targetParams = {}) => {
    setScreen(targetScreen);
    setParams(targetParams);
    // Keep currentScreen in sync so AiHelpButton always knows where the user is
    setCurrentScreen(targetScreen);
  }, []);

  // Load permissions from server after login or session restore
  const loadPermissions = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/auth/me');
      setPermissions(data.permissions || {});
    } catch (_) {}
  }, []);

  // logout — calls server to invalidate cookie, then clears local state.
  const logout = useCallback(async () => {
    await logoutUser();
    setUser(null);
    navigate('login');
  }, [navigate]);

  // Register logout with api.js so silent-refresh failures auto-logout
  useEffect(() => {
    setLogoutCallback(logout);
  }, [logout]);

  // Session restore on page reload
  useEffect(() => {
    const token = getToken();
    if (token) {
      const payload = decodeToken(token);
      if (payload && payload.exp > Date.now() / 1000) {
        setUser(payload);
        navigate('tables');
        hydrateFromAPI(apiClient);
        loadPermissions();
        return;
      }
    }

    // No valid in-memory token — try cookie-based silent restore
    fetch(`${BASE_URL}/auth/refresh`, {
      method:      'POST',
      credentials: 'include',
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data?.accessToken) return;
        setToken(data.accessToken);
        const payload = decodeToken(data.accessToken);
        if (payload) {
          setUser(payload);
          navigate('tables');
          hydrateFromAPI(apiClient);
          loadPermissions();
        }
      })
      .catch(() => {
        // Network error or server down — stay on login, don't crash
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = (accessToken, userData, forcePinChange = false, currentPin = '') => {
    setToken(accessToken);
    setUser(userData);
    hydrateFromAPI(apiClient);
    loadPermissions();
    if (forcePinChange) {
      setForcePinData({ currentPin });
      navigate('change-pin');
    } else {
      navigate('tables');
    }
  };

  const nav = { navigate, params, goBack: () => navigate('tables'), isOnline };

  const renderScreen = () => {
    switch (screen) {
      // ← CHANGED: login case now checks URL before deciding which screen to show.
      // If on a /v/<slug> URL → LoginScreen (reads slug from URL itself).
      // If on root or unknown URL → NoVenueScreen (prompt to enter venue name).
      case 'login':      return isVenueUrl ? <LoginScreen onLogin={login} /> : <NoVenueScreen />;
      case 'change-pin': return <ForcedPinChangeScreen currentPin={forcePinData?.currentPin || ''} onSuccess={() => { setForcePinData(null); navigate('tables'); }} />;
      case 'tables':     return <TablesScreen />;
      case 'order':     return <OrderScreen />;
      case 'cart':      return <CartScreen />;
      case 'confirmed': return <ConfirmedScreen />;
      case 'myorders':  return <MyOrdersScreen />;
      case 'dashboard': return <OwnerDashboardScreen />;
      default:          return <TablesScreen />;
    }
  };

  return (
    <AuthContext.Provider value={{ user, logout, permissions }}>
      <NavContext.Provider value={nav}>
        <div style={styles.app}>
          {renderScreen()}
          {/* AI assistant button — visible on all screens except login */}
          {screen !== 'login' && user && (
            <AiHelpButton currentScreen={currentScreen} role={user?.role} />
          )}
        </div>
      </NavContext.Provider>
    </AuthContext.Provider>
  );
}

const styles = {
  app: {
    backgroundColor: 'var(--bg)',
    minHeight: '100vh',
    color: 'var(--text)',
    fontFamily: "'Barlow Condensed', sans-serif",
    position: 'relative',
  },
};
